import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Quality Module Service Stores
 * Updated to match backend api-types
 */
export const qualityStores: ServiceStoreConfig[] = [
  {
    name: "compressive-strength-tests",
    options: {
      // Backend PK is testId, not id (compressive-strength-tests.class.ts:38).
      // Without this, dedup + .patch()/.remove() silently target the wrong key.
      idField: "testId",
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.compressiveStrengthTestsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalTests = entities.length;
          const passed = entities.filter(
            (test: Record<string, unknown>) => test.passFail === "pass"
          ).length;
          const failed = entities.filter(
            (test: Record<string, unknown>) => test.passFail === "fail"
          ).length;
          const avgStrengthPercent = entities.length > 0
            ? entities.reduce((sum: number, test: Record<string, unknown>) => {
                return sum + ((test.percentOfTarget as number) || 0);
              }, 0) / entities.length
            : 0;

          return [
            {
              title: "Total Tests",
              value: () => totalTests.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Passed",
              value: () => passed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
            {
              title: "Avg Strength %",
              value: () => `${(avgStrengthPercent as number).toFixed(1)}%`,
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "dimensional-inspections",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.dimensionalInspectionsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalInspections = entities.length;
          const withinTolerance = entities.filter(
            (inspection: Record<string, unknown>) => inspection.withinTolerance === true
          ).length;
          const outOfTolerance = entities.filter(
            (inspection: Record<string, unknown>) => inspection.withinTolerance === false
          ).length;
          const avgVariance = entities.length > 0
            ? entities.reduce((sum: number, inspection: Record<string, unknown>) => {
                return sum + Math.abs((inspection.variance as number) || 0);
              }, 0) / entities.length
            : 0;

          return [
            {
              title: "Total Inspections",
              value: () => totalInspections.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Within Tolerance",
              value: () => withinTolerance.toString(),
              valueColor: "success.main",
            },
            {
              title: "Out of Tolerance",
              value: () => outOfTolerance.toString(),
              valueColor: "error.main",
            },
            {
              title: "Avg Variance",
              value: () => (avgVariance as number).toFixed(3),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "fresh-concrete-tests",
    options: {
      // Backend PK is testId, not id (fresh-concrete-tests.class.ts:33).
      idField: "testId",
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.freshConcreteTestsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalTests = entities.length;
          const passed = entities.filter(
            (test: Record<string, unknown>) => test.passFail === "pass"
          ).length;
          const failed = entities.filter(
            (test: Record<string, unknown>) => test.passFail === "fail"
          ).length;
          const uniqueTestTypes = new Set(
            entities.map((test: Record<string, unknown>) => test.testType)
          ).size;

          return [
            {
              title: "Total Tests",
              value: () => totalTests.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Passed",
              value: () => passed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
            {
              title: "Test Types",
              value: () => uniqueTestTypes.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "material-certifications",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.materialCertificationsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalCertifications = entities.length;
          const active = entities.filter(
            (cert: Record<string, unknown>) => cert.status === "active" || cert.status === "valid"
          ).length;
          const expired = entities.filter(
            (cert: Record<string, unknown>) => cert.status === "expired"
          ).length;
          const expiringSoon = entities.filter((cert: Record<string, unknown>) => {
            if (!cert.expirationDate) return false;
            const expDate = new Date(cert.expirationDate as string);
            const now = new Date();
            const thirtyDaysFromNow = new Date();
            thirtyDaysFromNow.setDate(now.getDate() + 30);
            return expDate > now && expDate <= thirtyDaysFromNow;
          }).length;

          return [
            {
              title: "Total Certifications",
              value: () => totalCertifications.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => active.toString(),
              valueColor: "success.main",
            },
            {
              title: "Expired",
              value: () => expired.toString(),
              valueColor: "error.main",
            },
            {
              title: "Expiring Soon",
              value: () => expiringSoon.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "panel-rejections",
    options: {
      // Backend PK is rejectionId, not id (panel-rejections.class.ts:36).
      idField: "rejectionId",
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "non-conformance-reports",
    options: {
      // Backend PK is ncrId, not id (non-conformance-reports.class.ts:38).
      idField: "ncrId",
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.nonConformanceReportsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalNCRs = entities.length;
          const open = entities.filter(
            (ncr: Record<string, unknown>) => ncr.status === "open" || ncr.status === "in_progress"
          ).length;
          const closed = entities.filter(
            (ncr: Record<string, unknown>) => ncr.status === "closed"
          ).length;
          const highSeverity = entities.filter(
            (ncr: Record<string, unknown>) => ncr.severity === "high" || ncr.severity === "critical"
          ).length;

          return [
            {
              title: "Total NCRs",
              value: () => totalNCRs.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Open",
              value: () => open.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Closed",
              value: () => closed.toString(),
              valueColor: "success.main",
            },
            {
              title: "High Severity",
              value: () => highSeverity.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "qc-inspections",
    options: {
      // Backend PK is inspectionId, not id (qc-inspections.class.ts:31).
      idField: "inspectionId",
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.qcInspectionsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalInspections = entities.length;
          const passed = entities.filter(
            (inspection: Record<string, unknown>) => inspection.passFail === "pass"
          ).length;
          const failed = entities.filter(
            (inspection: Record<string, unknown>) => inspection.passFail === "fail"
          ).length;
          const correctiveActionRequired = entities.filter(
            (inspection: Record<string, unknown>) => inspection.correctiveActionRequired === true
          ).length;

          return [
            {
              title: "Total Inspections",
              value: () => totalInspections.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Passed",
              value: () => passed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
            {
              title: "Corrective Action",
              value: () => correctiveActionRequired.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "qc-samples-submitted",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.qcSamplesSubmittedStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalSamples = entities.length;
          const thisWeek = entities.filter((sample: Record<string, unknown>) => {
            const submitDate = new Date(sample.submittedDate as string);
            const now = new Date();
            const weekAgo = new Date();
            weekAgo.setDate(now.getDate() - 7);
            return submitDate > weekAgo;
          }).length;
          const pending = entities.filter(
            (sample: Record<string, unknown>) => sample.status === "pending" || sample.status === "submitted"
          ).length;
          const completed = entities.filter(
            (sample: Record<string, unknown>) => sample.status === "completed" || sample.status === "tested"
          ).length;

          return [
            {
              title: "Total Samples",
              value: () => totalSamples.toString(),
              valueColor: "primary.main",
            },
            {
              title: "This Week",
              value: () => thisWeek.toString(),
              valueColor: "info.main",
            },
            {
              title: "Pending Tests",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Completed",
              value: () => completed.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "quality-summary",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.qualitySummaryStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const latestSummary = entities.length > 0 ? (entities[0] as Record<string, unknown>) : null;
          const inspectionPassRate = latestSummary?.passRatePercent || 0;
          const ncrOpenBalance = latestSummary?.ncrsOpenBalance || 0;
          const concretePassRate = latestSummary?.concretePassRatePercent || 0;
          const avgNcrCloseDays = latestSummary?.avgNcrCloseDays || 0;

          return [
            {
              title: "Inspection Pass Rate",
              value: () => `${(inspectionPassRate as number).toFixed(1)}%`,
              valueColor: "success.main",
            },
            {
              title: "NCR Open Balance",
              value: () => (ncrOpenBalance as number).toString(),
              valueColor: "warning.main",
            },
            {
              title: "Concrete Pass Rate",
              value: () => `${(concretePassRate as number).toFixed(1)}%`,
              valueColor: "primary.main",
            },
            {
              title: "Avg NCR Close Days",
              value: () => (avgNcrCloseDays as number).toFixed(1),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "test-samples",
    options: {
      // Backend PK is sampleId, not id (test-samples.class.ts:26).
      idField: "sampleId",
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        summaryCards: (rootStore) => {
          const store = rootStore.testSamplesStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalSamples = entities.length;
          const totalCylinders = entities.reduce((sum: number, sample: Record<string, unknown>) => {
            return sum + ((sample.numberOfCylinders as number) || 0);
          }, 0);
          const uniqueProjects = new Set(
            entities.map((sample: Record<string, unknown>) => sample.projectId)
          ).size;
          const thisMonth = entities.filter((sample: Record<string, unknown>) => {
            const sampleDate = new Date(sample.sampleDatetime as string);
            const now = new Date();
            return (
              sampleDate.getMonth() === now.getMonth() &&
              sampleDate.getFullYear() === now.getFullYear()
            );
          }).length;

          return [
            {
              title: "Total Samples",
              value: () => totalSamples.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Total Cylinders",
              value: () => totalCylinders.toString(),
              valueColor: "info.main",
            },
            {
              title: "Unique Projects",
              value: () => uniqueProjects.toString(),
              valueColor: "success.main",
            },
            {
              title: "This Month",
              value: () => thisMonth.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  // Concrete-testing reference data (added 2026-06-01). These are small
  // read-mostly lookups — UI dropdowns and the Reference Data admin tab
  // read from them. enableStorage caches them in MobX so dialogs render
  // instantly.
  {
    name: "cylinder-sizes",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "cylinder-ages",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "fracture-types",
    options: {
      // Backend PK is code, not id (fracture-types.class.ts:25).
      idField: "code",
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "concrete-test-config",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "test-sample-panels",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // Compliance document register (added 2026-08-24, roadmap QC rows 11, 17,
  // 24, 25, 31). Two stores because the page is a grid: TYPES are the rows
  // (what must be on file, how often), DOCUMENTS are the cells (what has
  // actually been filed for each period).
  //
  // enableStorage is OFF for both, unlike the reference-data stores above.
  // enableStorage persists entities to IndexedDB (ServiceStore.ts:172, via
  // localforage) and rehydrates them at boot, and a ServiceStore's `entities`
  // only ever grows — a find never evicts a record the server has stopped
  // returning. For a fixed lookup like cylinder-sizes that is a harmless warm
  // cache. For a register whose rows are created and deleted by users it is a
  // correctness bug: a requirement deleted on the server survived a hard
  // refresh in every browser that had ever loaded it, and clicking delete on
  // the ghost returned "No record found". Seen on production 2026-08-27.
  //
  // The register's screens now read find results directly and never touch
  // these stores, so there is nothing to cache and nothing to go stale.
  {
    name: "compliance-document-types",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
    },
  },
  {
    name: "compliance-documents",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
    },
  },
  // Aggregate master + daily moisture log (added 2026-09-22, roadmap QC
  // row 45). Replaces the MOISTURES/AGGREGATE tabs of Premier's absorption
  // workbook.
  //
  // enableStorage is OFF for both, for the same reason as the compliance
  // stores above. `aggregates` is edited and deleted from the Reference Data
  // tab, so a cached entity that the server has stopped returning is a ghost
  // row the user cannot delete. The moisture log is transactional and is read
  // a day at a time — there is nothing to warm-cache.
  {
    name: "aggregates",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
    },
  },
  {
    name: "aggregate-moisture-tests",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
    },
  },
  // QC inspection checklist items lookup (added 2026-06-09 for the
  // InspectionEventDialog two-column Premier/PCI checklist). Admin-editable
  // via the Reference Data tab. Stays on the plain ServiceStore — do NOT add
  // this entry to autoOfflineStores in ServiceStoreFactory.ts.
  {
    name: "qc-checklist-items",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // QC failure-reason lookup (added 2026-06-15 for the InspectionEventDialog
  // failure-category multi-select). Keyed by (inspectionStage, passFailContext).
  // Do NOT add to autoOfflineStores.
  {
    name: "qc-failure-reasons",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // QC remediation-stamp lookup (added 2026-06-15 for the post-pour
  // pass-with-deficiencies flow). Do NOT add to autoOfflineStores.
  {
    name: "qc-remediation-stamps",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
];
